/**
 * 创建页「选拍法(可选)」—— 波2(2026-07-25)。
 *
 * ═══ 这一层原来是看不见的 ═══════════════════════════════════════════════════════
 * 引擎一直在选拍法:`select_formula` 按目的 + 文案正则从 `formulas.yaml` 53 张卡里确定性
 * 挑一张,节拍表/口播公式随之进架构师与导演的 prompt。用户看不到、也换不了 —— 「为什么这条
 * 片是这么讲的」全靠猜。波2 把这一层暴露成可选下钻:**默认收起 = 引擎自动(与波2前逐字一致)**,
 * 想换的人展开卡墙点一张,`formulaKey` 随编排请求上送(方案 D3)。
 *
 * ═══ 三条不肯让步的口径 ═════════════════════════════════════════════════════════
 * ① **不选是常态,不是缺省错误**。所以整块折叠、标题带「可选」、收起时明说「引擎自动」——
 *    不做成必填第二道坎(方案 §5 D3:自由的人跳过、引导的人展开)。
 * ② **拉不到清单不挡路**:taxonomy 是静态投影,挂了就退回「引擎自动」并说清楚,
 *    绝不把创建流程卡在一个可选面板上。
 * ③ 排序 = 默认牌第一、其余按库内声明序(0914 拍板:「我想让观众▸」透镜重排整体退役 ——
 *    它与文案框里「想让观众做什么」逐字重复,且 v2 后每目的只露 3 张卡,重排已无意义;
 *    卡面上的 drives chips 同批一并撤下)。
 */
import { useMemo, useState } from "react";
import { ChevronDown, Loader2, Sparkles, Wand2 } from "lucide-react";

import { Badge } from "@/shared/ui/badge";
import { Button } from "@/shared/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/shared/ui/collapsible";
import { humanizeInternalTerms } from "@/shared/lib/internalCodes";
import { cn } from "@/shared/lib/utils";

import type { VideoFormulaCardDto } from "../../api/videoService";
import { useCreativeTaxonomy } from "../../model/useCreativeTaxonomy";
import type { ToneRegister } from "../../model/toneRegister";

interface Props {
  /** 当前视频目的(`form.briefPurpose`);为空 = 还没选目的,卡墙无从谈起。 */
  purpose?: string | null;
  /**
   * 组内档位并集(2026-08-10 四组卡):传了就展示这些档的拍法卡**并集**(同 key 去重),
   * 选中非当前档的卡时经 `onChange` 第三参回传所属档,由上层自动换档 —— 引擎 selector
   * 有「卡必须属当前目的」的闸,并集靠换档过闸而不是拆闸。缺省 = 只看 `purpose` 单档(旧行为)。
   */
  groupPurposes?: string[];
  /** 已选拍法 key(`form.pickedFormulaKey`);null = 引擎自动。 */
  value?: string | null;
  /**
   * 选中/清除。**name 一并回传**:来源条与详情页要显示的是人读的拍法名,
   * 而那两处都拿不到 taxonomy —— 不回传就得让它们各自再拉一次清单(第二份数据源)。
   * `sourcePurpose`:这张卡所属的目的档(并集模式下可能 ≠ 当前档,上层据此换档);清除时 null。
   */
  onChange: (
    formulaKey: string | null,
    formulaName: string | null,
    sourcePurpose?: string | null
  ) => void;
  /** 编排/出图进行中:整块只读(brief 已被服务端快照,这时候换拍法不生效)。 */
  disabled?: boolean;
  /**
   * 紧凑形态(2026-08-06 重排):作为**目的行的子行**内嵌,不再是页面底部一张独立卡。
   *
   * ★为什么是形态而不是新组件:拍法的全部逻辑(taxonomy 拉取、默认牌排序、autoOnly 档、
   *   拉不到不挡路)都在本文件里,复制一份出去就是第二份判据,必漂移。外壳换、内核不动。
   *   `data-video-formula-picker` 钩子两种形态都保留 —— e2e 定位不受形态影响。
   */
  compact?: boolean;
  /**
   * 调性档(0924 车 C):taxonomy 请求带 `?register=`,luxury 档 ai 优先推质感类拍法。
   * 缺省 = hardsell(与 ai 缺省一致)。
   */
  register?: ToneRegister | null;
}

/**
 * 节拍串里的 `{槽位}` 去掉花括号:卡面是给人读结构的,不是给人读模板语法的。
 * 导出:`VideoShootPlanCard` 的节拍带按同一份规则渲染拍法 beats(不另抄一份去括号逻辑)。
 */
export function humanizeBeat(beat: string): string {
  // 1004 T10:拍法 beats 来自 ai 拍法清单,原文混着「hero 镜」「i2i」这类内部术语,对客换中文
  return humanizeInternalTerms(beat.replace(/[{}]/g, ""));
}

/**
 * 拉不到清单时的口径 —— 收起行与展开面板共用**同一份**(VID-PUR-09):
 * 两处各写一遍,哪天只改了一处措辞,失败这件事就有两套说法。
 */
const TAXONOMY_FAILED_TEXT = "拍法清单没读到,这条片仍会由引擎自动选拍法出片。";

export function VideoFormulaPicker({
  purpose,
  groupPurposes,
  value,
  onChange,
  disabled,
  compact,
  register,
}: Props) {
  const [open, setOpen] = useState(false);

  // 静态投影(目的×卡),一个会话拉一次就够。
  const taxonomyQuery = useCreativeTaxonomy(register);

  const group = useMemo(() => {
    const purposes = taxonomyQuery.data?.purposes;
    if (!Array.isArray(purposes) || !purpose) return null;
    // 并集模式(四组卡):组内各档的卡合并展示,同 key 去重(共有卡归当前档,换档不误触发)。
    const wanted = groupPurposes?.length ? groupPurposes : [purpose];
    const hits = purposes.filter((p) => wanted.includes(p.purpose));
    if (hits.length === 0) return null;
    // 当前档排前:它的默认卡/排序习惯保持旧观感,另一档的卡跟在后面。
    hits.sort((a, b) => (a.purpose === purpose ? -1 : 0) - (b.purpose === purpose ? -1 : 0));
    const seen = new Set<string>();
    const formulas: (VideoFormulaCardDto & { sourcePurpose: string })[] = [];
    for (const h of hits) {
      for (const c of h.formulas) {
        if (seen.has(c.key)) continue;
        seen.add(c.key);
        // ★isDefault 只保留当前档的(并集里两档各有默认卡,都标「引擎默认」= 自相矛盾)
        formulas.push({
          ...c,
          isDefault: c.isDefault && h.purpose === purpose,
          sourcePurpose: h.purpose,
        });
      }
    }
    return { autoOnly: hits.every((h) => h.autoOnly), formulas };
  }, [taxonomyQuery.data, purpose, groupPurposes]);

  /**
   * 排序:默认牌第一、其余按 taxonomy 里的声明序(0914 拍板,见头注③ —— 透镜重排整体退役,
   * 不再按用户点的透镜临时重排)。
   */
  const cards = useMemo(() => {
    const list = group?.formulas ?? [];
    return [...list]
      .map((c, i) => ({ c, i }))
      .sort((a, b) => (b.c.isDefault ? 1 : 0) - (a.c.isDefault ? 1 : 0) || a.i - b.i)
      .map(({ c }) => c);
  }, [group]);

  const selected = cards.find((c) => c.key === value) ?? null;
  // 引擎默认那张(不选时引擎会挑它);收起态说清「引擎自动 · 默认『X』」(A1:让默认拍法可见)。
  const defaultCard = group?.formulas.find((c) => c.isDefault) ?? null;

  // F 批:autoOnly 档(硬广这类「引擎直接按产品优势编排、本就没有可手选拍法」的目的)。
  // 收起行原来是一句 text-2xs 灰字「本档由引擎直接编排」,太淡,看着像"坏了/一片空白"。
  // 这个态改用清晰可读的说明 + 主色点缀(下两处形态共用同一份措辞),让用户一眼看懂
  // "不是坏了、是这档不用选拍法"。★只改这个态的视觉可读性,不动 autoOnly 判定(group.autoOnly)、
  //   也不动展开面板里那段说明(仍可展开看)。
  const autoOnly = !selected && !taxonomyQuery.isError && !!group?.autoOnly;
  const autoOnlyLabel = (
    <span className="inline-flex items-center gap-1 rounded-md bg-primary/5 px-2 py-0.5 text-xs font-medium text-foreground">
      <Wand2 className="h-3 w-3 shrink-0 text-primary" strokeWidth={1.8} />
      引擎按产品优势直接编排(无需选)
    </span>
  );

  // 收起时的一行状态:说清「现在到底按什么拍」。
  // ★失败不许被折叠面板吞掉(VID-PUR-09):失败文案与重试原本只活在 CollapsibleContent 里,
  //   而 Radix 收起时子树根本不挂载 —— 默认收起的人看到的收起行仍是「引擎自动」,与正常态
  //   一模一样,失败被整个静默。收起行如实说失败,重试就地给(见下两处形态分支)。
  const summary = selected
    ? `已选「${selected.name}」`
    : taxonomyQuery.isError
      ? TAXONOMY_FAILED_TEXT
      : group?.autoOnly
        ? "本档由引擎直接编排"
        : defaultCard
          ? `引擎按你的文案自动选(默认「${defaultCard.name}」)`
          : "引擎按你的文案自动选";

  return (
    <Collapsible
      open={open}
      onOpenChange={setOpen}
      data-video-formula-picker
      className={cn(
        compact
          ? "pt-2"
          : cn(
              "rounded-lg border bg-card/40 px-3.5 py-2.5",
              selected ? "border-primary/50 bg-primary/5" : "border-border/60"
            ),
        disabled && "pointer-events-none opacity-60"
      )}
    >
      {compact ? (
        /* 紧凑形态:一行小字挂在目的 chip 下面。「拍法是目的的下钻」这句话本来只写在注释里,
           现在让 DOM 说同一句 —— 它是目的行的子节点,不再是页面底部一张平级的卡。 */
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-2xs">
          <span className="text-muted-foreground">拍法</span>
          {autoOnly ? (
            autoOnlyLabel
          ) : (
            <span className="font-medium text-foreground">{summary}</span>
          )}
          {/* 手选卡亮出适用条件(2026-08-10 引导批):400 电话真单选了「品牌宣言体」(适合
              品牌理念文案)却贴业务资料 —— 适配条件此前只在展开的卡面上,选完就看不见了。 */}
          {selected?.when && (
            <span className="text-muted-foreground">
              适合:{selected.when} · 文案对不上时引擎自动选更稳
            </span>
          )}
          {selected && (
            <button
              type="button"
              onClick={() => onChange(null, null)}
              className="cursor-pointer rounded text-muted-foreground underline decoration-dotted underline-offset-2 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              改回自动
            </button>
          )}
          {taxonomyQuery.isError ? (
            /* 失败态:重试就地给,「换一张」隐掉 —— 展开面板里也只有同一句失败文案,
               留着入口就是请人进一条死胡同(VID-PUR-09) */
            <button
              type="button"
              onClick={() => void taxonomyQuery.refetch()}
              className="inline-flex cursor-pointer items-center gap-0.5 rounded text-primary underline decoration-dashed underline-offset-2 hover:brightness-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              重试
            </button>
          ) : (
            <CollapsibleTrigger asChild>
              <button
                type="button"
                className="inline-flex cursor-pointer items-center gap-0.5 rounded text-primary underline decoration-dashed underline-offset-2 hover:brightness-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              >
                {open ? "收起" : "换一张"}
                <ChevronDown className={cn("h-3 w-3 transition-transform", open && "rotate-180")} />
              </button>
            </CollapsibleTrigger>
          )}
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <Wand2
            className={cn("h-4 w-4 shrink-0", selected ? "text-primary" : "text-muted-foreground")}
            strokeWidth={1.8}
          />
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium text-foreground">
              选拍法 <span className="text-xs font-normal text-muted-foreground">(可选)</span>
            </div>
            {autoOnly ? (
              <div className="mt-0.5">{autoOnlyLabel}</div>
            ) : (
              <div className="truncate text-2xs text-muted-foreground">{summary}</div>
            )}
          </div>
          {selected && (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="h-7 shrink-0 text-xs"
              onClick={() => onChange(null, null)}
            >
              改回自动
            </Button>
          )}
          {taxonomyQuery.isError ? (
            /* 同 compact 形态:失败态就地重试,不再给「换个拍法」这个空入口(VID-PUR-09) */
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="h-7 shrink-0 text-xs"
              onClick={() => void taxonomyQuery.refetch()}
            >
              重试
            </Button>
          ) : (
            <CollapsibleTrigger asChild>
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="h-7 shrink-0 gap-1 text-xs"
              >
                {open ? "收起" : "换个拍法"}
                <ChevronDown className={cn("h-3 w-3 transition-transform", open && "rotate-180")} />
              </Button>
            </CollapsibleTrigger>
          )}
        </div>
      )}

      <CollapsibleContent className="mt-3 space-y-3">
        {/* ① 还没选目的:卡墙按目的分组,没有目的就没有「该目的下的拍法」这回事 */}
        {!purpose ? (
          <p className="text-xs text-muted-foreground">先在上方选「视频目的」,再来挑拍法。</p>
        ) : taxonomyQuery.isPending ? (
          <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Loader2 className="h-3 w-3 animate-spin" />
            正在读取拍法清单…
          </p>
        ) : taxonomyQuery.isError || !taxonomyQuery.data?.purposes ? (
          // ③ 拉不到不挡路:说清后果(仍能出片,只是没得挑),不是一句红字了事
          <p className="text-xs text-muted-foreground">
            {TAXONOMY_FAILED_TEXT}
            <button
              type="button"
              className="ml-1 underline underline-offset-2 hover:text-foreground"
              onClick={() => void taxonomyQuery.refetch()}
            >
              重试
            </button>
          </p>
        ) : !group || group.autoOnly || cards.length === 0 ? (
          // 「产品硬广」这类档在库里本就没有它自己的卡(引擎走 feature_forward 直接编排)——
          // 说清楚是「没有可以手选的」,而不是渲个空墙让人以为没加载出来。
          // ★措辞不能写成「没有拍法」(codex review P2):这档的架构师确实不套卡,但节拍层仍会由
          //   引擎自动挑一张并记进成片档案 —— 详情页会显示它。这里说死「没有」,两个界面就各说一套。
          <p className="text-xs text-muted-foreground">
            「{purpose}」这一档由引擎直接按产品优势编排,没有可以手选的拍法。
          </p>
        ) : (
          <>
            <div className="grid max-h-[420px] grid-cols-1 gap-2 overflow-y-auto pr-1 sm:grid-cols-2">
              {cards.map((c) => {
                const active = c.key === value;
                return (
                  <button
                    key={c.key}
                    type="button"
                    aria-pressed={active}
                    onClick={() => {
                      if (active) {
                        onChange(null, null, null);
                        return;
                      }
                      onChange(c.key, c.name, c.sourcePurpose);
                      // A1「选完自动收起」:挑定一张就把卡墙收回,回到收起态显示所选拍法名。
                      setOpen(false);
                    }}
                    className={cn(
                      "rounded-[calc(var(--radius)*0.75)] border p-2.5 text-left transition-colors",
                      active
                        ? "border-primary bg-primary/10"
                        : "border-border/60 bg-background/40 hover:border-primary/40 hover:bg-accent/40"
                    )}
                  >
                    <div className="flex items-center gap-1.5">
                      <span className="truncate text-sm font-medium text-foreground">{c.name}</span>
                      {c.isDefault && (
                        <Badge variant="secondary" className="shrink-0 text-2xs">
                          引擎默认
                        </Badge>
                      )}
                      {/* 并集模式:另一档的卡明说「选它会换档」—— 自动换档不许静默 */}
                      {c.sourcePurpose !== purpose && (
                        <Badge variant="outline" className="shrink-0 text-2xs text-primary">
                          按「{c.sourcePurpose}」拍
                        </Badge>
                      )}
                      {active && <Sparkles className="ml-auto h-3.5 w-3.5 shrink-0 text-primary" />}
                    </div>
                    <p className="mt-1 line-clamp-2 text-2xs text-muted-foreground">{c.when}</p>
                    {c.beats?.length > 0 && (
                      <p className="mt-1 line-clamp-2 text-2xs text-muted-foreground/80">
                        {c.beats.map(humanizeBeat).join(" · ")}
                      </p>
                    )}
                  </button>
                );
              })}
            </div>

            <p className="text-2xs text-muted-foreground">
              不选 = 引擎按你的文案自动挑一张。
              {/* 诚实标注:粘的是已确认分镜脚本时,引擎忠实拆解不套拍法(判据在服务端,
                  前端不复刻 looks_like_script 那套正则 —— 复刻就是第二份判据,必然漂移)。 */}
              贴的是现成分镜脚本时,系统忠实拆解、不套拍法。
            </p>
          </>
        )}
      </CollapsibleContent>
    </Collapsible>
  );
}
